import styled, { keyframes, css } from "styled-components";

/* =============================================================
   IA SISTEMAS — Design System Premium
   Modo claro · cantos retos · tipografia Inter · dourado Golden
   ============================================================= */

const GOLD = "#B17D2A";
const GOLD_DARK = "#8f6522";
const GOLD_TINT = "#faf6ee";
const GOLD_BORDER = "#e9dcc3";
const BG = "#f7f7f8";
const SURFACE = "#ffffff";
const BORDER = "#e7e8ea";
const BORDER_SOFT = "#f0f1f2";
const TEXT = "#16181d";
const TEXT_SEC = "#5f6570";
const TEXT_MUTED = "#9298a3";
const RADIUS = "2px";
const FONT = `"Inter", "Segoe UI", -apple-system, Roboto, sans-serif`;

const SHADOW_SM = "0 1px 2px rgba(22, 24, 29, 0.04)";
const SHADOW_MD =
  "0 1px 2px rgba(22, 24, 29, 0.04), 0 4px 12px rgba(22, 24, 29, 0.05)";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const scrollbars = css`
  scrollbar-width: thin;
  scrollbar-color: #d5d7db transparent;
  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #d5d7db;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #b9bcc2;
  }
`;

export const Page = styled.div`
  min-height: 100vh;
  background: ${BG};
  color: ${TEXT};
  font-family: ${FONT};
  -webkit-font-smoothing: antialiased;
  letter-spacing: -0.011em;

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
`;

/* ---------------- NAVBAR ---------------- */

export const Navbar = styled.header`
  background: ${SURFACE};
  border-bottom: 1px solid ${BORDER};
  padding: 0 36px 0 92px;
  position: sticky;
  top: 0;
  z-index: 50;
`;

export const NavbarTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 0 16px 0;
  flex-wrap: wrap;
  gap: 12px;
`;

export const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

export const BrandMark = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #c8963e 0%, ${GOLD} 55%, ${GOLD_DARK} 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0.04em;
  border-radius: ${RADIUS};
  box-shadow: ${SHADOW_SM};
`;

export const BrandText = styled.div`
  h1 {
    margin: 0;
    font-size: 17px;
    font-weight: 800;
    letter-spacing: -0.02em;
    color: ${TEXT};
    line-height: 1.2;
  }
  small {
    display: block;
    font-size: 11.5px;
    font-weight: 500;
    color: ${TEXT_MUTED};
    margin-top: 2px;
    letter-spacing: 0.01em;
  }
`;

export const NavbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

export const UpdateChip = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${SURFACE};
  border: 1px solid ${BORDER};
  border-radius: 999px;
  padding: 6px 14px;
  font-family: ${FONT};
  font-size: 12px;
  font-weight: 600;
  color: ${TEXT_SEC};
  cursor: pointer;
  transition: all 0.15s ease;

  .icone {
    display: inline-flex;
    color: ${GOLD};
    font-size: 14px;
    transition: transform 0.4s ease;
  }

  &:hover {
    border-color: ${GOLD_BORDER};
    background: ${GOLD_TINT};
    color: ${TEXT};
  }
  &:hover .icone {
    transform: rotate(180deg);
  }
  &:active .icone {
    transform: rotate(360deg);
  }

  time {
    font-variant-numeric: tabular-nums;
    font-weight: 700;
    color: ${TEXT};
  }
`;

export const StatusPill = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
  color: ${(p) => (p.$offline ? "#b42318" : "#087443")};
  background: ${(p) => (p.$offline ? "#fef3f2" : "#f0faf4")};
  border: 1px solid ${(p) => (p.$offline ? "#fecdca" : "#c6ecd5")};
  border-radius: 999px;
  padding: 6px 14px 6px 11px;

  &::before {
    content: "";
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: currentColor;
    box-shadow: 0 0 0 3px
      ${(p) => (p.$offline ? "rgba(180,35,24,0.12)" : "rgba(8,116,67,0.14)")};
  }
`;

export const NavMenu = styled.nav`
  display: flex;
  gap: 26px;
  overflow-x: auto;
  ${scrollbars}
`;

export const NavItem = styled.button`
  position: relative;
  background: transparent;
  border: none;
  padding: 13px 2px 15px 2px;
  font-family: ${FONT};
  font-size: 13.5px;
  font-weight: ${(p) => (p.$active ? 700 : 500)};
  letter-spacing: -0.01em;
  color: ${(p) => (p.$active ? TEXT : TEXT_SEC)};
  cursor: pointer;
  white-space: nowrap;
  transition: color 0.15s ease;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1px;
    height: 2px;
    background: ${GOLD};
    transform: scaleX(${(p) => (p.$active ? 1 : 0)});
    transform-origin: left center;
    transition: transform 0.22s ease;
  }

  &:hover {
    color: ${TEXT};
  }
  &:hover::after {
    transform: scaleX(1);
    background: ${(p) => (p.$active ? GOLD : "#d8c395")};
  }
`;

export const Content = styled.main`
  padding: 32px 36px 56px 36px;
  max-width: 1520px;
  margin: 0 auto;
  animation: ${fadeUp} 0.32s ease both;
`;

/* ---------------- DASHBOARD ---------------- */

export const SectionTitle = styled.h2`
  font-size: 11.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  color: ${TEXT_MUTED};
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 10px;

  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: ${BORDER};
  }
`;

export const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(215px, 1fr));
  gap: 14px;
  margin-bottom: 36px;
`;

export const KpiCard = styled.div`
  background: ${SURFACE};
  border: 1px solid ${BORDER};
  border-radius: ${RADIUS};
  box-shadow: ${SHADOW_SM};
  padding: 20px 22px 18px 22px;
  position: relative;
  overflow: hidden;
  transition: box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: ${(p) => p.$accent || GOLD};
  }

  &:hover {
    box-shadow: ${SHADOW_MD};
    transform: translateY(-1px);
    border-color: #d9dbdf;
  }

  label {
    display: block;
    font-size: 10.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.09em;
    color: ${TEXT_MUTED};
    margin-bottom: 12px;
  }

  strong {
    font-size: 34px;
    font-weight: 800;
    letter-spacing: -0.03em;
    color: ${TEXT};
    font-variant-numeric: tabular-nums;
    line-height: 1;
  }

  span {
    display: block;
    font-size: 12px;
    font-weight: 500;
    color: ${TEXT_SEC};
    margin-top: 10px;
  }
`;

export const TwoCols = styled.div`
  display: grid;
  grid-template-columns: 1.45fr 1fr;
  gap: 14px;
  margin-bottom: 36px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

export const Panel = styled.div`
  background: ${SURFACE};
  border: 1px solid ${BORDER};
  border-radius: ${RADIUS};
  box-shadow: ${SHADOW_SM};
  padding: 24px 26px;

  h3 {
    margin: 0 0 20px 0;
    font-size: 14px;
    font-weight: 700;
    letter-spacing: -0.015em;
    color: ${TEXT};
  }
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th {
    text-align: left;
    font-size: 10.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: ${TEXT_MUTED};
    padding: 10px 14px;
    border-bottom: 1px solid ${BORDER};
    background: #fbfbfc;

    &:first-child {
      border-radius: ${RADIUS} 0 0 0;
    }
    &:last-child {
      border-radius: 0 ${RADIUS} 0 0;
    }
  }

  td {
    padding: 14px;
    border-bottom: 1px solid ${BORDER_SOFT};
    color: ${TEXT_SEC};
    font-weight: 500;
    vertical-align: middle;
    font-variant-numeric: tabular-nums;
  }

  td.titulo {
    color: ${TEXT};
    font-weight: 600;
    max-width: 280px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  td.num {
    color: ${TEXT_MUTED};
    font-weight: 700;
    font-size: 12px;
  }

  td.seta {
    color: ${TEXT_MUTED};
    font-size: 11px;
    text-align: right;
    width: 36px;
    transition: transform 0.2s ease;
  }

  tbody tr.clicavel {
    cursor: pointer;
    transition: background 0.12s ease;
  }
  tbody tr.clicavel:hover {
    background: #fafafa;
  }
  tbody tr.aberta {
    background: ${GOLD_TINT};
  }
  tbody tr.aberta td {
    border-bottom-color: transparent;
  }
  tbody tr.detalhe > td {
    padding: 0 14px 18px 14px;
    background: ${GOLD_TINT};
  }
  tr:last-child td {
    border-bottom: none;
  }
`;

export const StatusChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.02em;
  padding: 3px 9px;
  border-radius: ${RADIUS};
  white-space: nowrap;
  background: ${(p) =>
    ({
      pendente: "#fffaeb",
      analise: "#eff6ff",
      resolvido: "#f0faf4",
      cancelado: "#f4f5f6",
      urgente: "#fef3f2",
      paused: "#fffaeb",
      blocked: "#fef3f2",
      budget: GOLD_TINT,
      tema: "#eff6ff",
      negativo: "#fef3f2",
      positivo: "#f0faf4",
      neutro: "#f4f5f6",
    }[p.$tipo] || "#f4f5f6")};
  color: ${(p) =>
    ({
      pendente: "#b54708",
      analise: "#1d4ed8",
      resolvido: "#087443",
      cancelado: "#5f6570",
      urgente: "#b42318",
      paused: "#b54708",
      blocked: "#b42318",
      budget: GOLD_DARK,
      tema: "#1d4ed8",
      negativo: "#b42318",
      positivo: "#087443",
      neutro: "#5f6570",
    }[p.$tipo] || "#5f6570")};
  border: 1px solid
    ${(p) =>
      ({
        pendente: "#fce9bb",
        analise: "#d5e3fb",
        resolvido: "#c6ecd5",
        cancelado: "#e7e8ea",
        urgente: "#fbd5d1",
        paused: "#fce9bb",
        blocked: "#fbd5d1",
        budget: GOLD_BORDER,
        tema: "#d5e3fb",
        negativo: "#fbd5d1",
        positivo: "#c6ecd5",
        neutro: "#e7e8ea",
      }[p.$tipo] || "#e7e8ea")};

  &::before {
    content: "";
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: currentColor;
    opacity: 0.85;
  }
`;

export const MiniBarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 14px;

  .label {
    width: 92px;
    font-size: 12.5px;
    font-weight: 600;
    color: ${TEXT_SEC};
  }
  .track {
    flex: 1;
    height: 6px;
    background: ${BORDER_SOFT};
    border-radius: 1px;
    overflow: hidden;
  }
  .fill {
    height: 100%;
    background: linear-gradient(90deg, #c8963e, ${GOLD});
    transition: width 0.6s cubic-bezier(0.22, 1, 0.36, 1);
  }
  .value {
    width: 56px;
    text-align: right;
    font-size: 12.5px;
    font-weight: 700;
    color: ${TEXT};
    font-variant-numeric: tabular-nums;
  }
`;

/* ---------------- CONVERSAS ---------------- */

export const ChatLayout = styled.div`
  display: grid;
  grid-template-columns: 360px 1fr;
  gap: 14px;
  height: calc(100vh - 225px);
  min-height: 540px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    height: auto;
  }
`;

export const ChatListPanel = styled.div`
  background: ${SURFACE};
  border: 1px solid ${BORDER};
  border-radius: ${RADIUS};
  box-shadow: ${SHADOW_SM};
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const SearchInput = styled.input`
  margin: 14px 14px 10px 14px;
  padding: 10px 13px;
  background: ${BG};
  border: 1px solid ${BORDER};
  border-radius: ${RADIUS};
  color: ${TEXT};
  font-family: ${FONT};
  font-size: 13px;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;

  &:focus {
    background: ${SURFACE};
    border-color: ${GOLD};
    box-shadow: 0 0 0 3px rgba(177, 125, 42, 0.1);
  }
  &::placeholder {
    color: ${TEXT_MUTED};
  }
`;

export const FilterRow = styled.div`
  display: flex;
  gap: 10px;
  padding: 0 14px 12px 14px;
  align-items: center;
  border-bottom: 1px solid ${BORDER_SOFT};

  select {
    flex: 1;
    background: ${BG};
    border: 1px solid ${BORDER};
    border-radius: ${RADIUS};
    color: ${TEXT};
    font-family: ${FONT};
    padding: 8px 10px;
    font-size: 12px;
    font-weight: 500;
    outline: none;
    cursor: pointer;
    transition: border-color 0.15s ease;

    &:focus {
      border-color: ${GOLD};
    }
  }

  label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11.5px;
    font-weight: 600;
    color: ${TEXT_SEC};
    cursor: pointer;
    white-space: nowrap;
    user-select: none;

    input {
      accent-color: ${GOLD};
      cursor: pointer;
    }
  }
`;

export const ChatListScroll = styled.div`
  overflow-y: auto;
  flex: 1;
  ${scrollbars}
`;

export const ChatListItem = styled.div`
  padding: 13px 16px;
  border-bottom: 1px solid ${BORDER_SOFT};
  border-left: 2px solid ${(p) => (p.$selected ? GOLD : "transparent")};
  background: ${(p) => (p.$selected ? GOLD_TINT : "transparent")};
  cursor: pointer;
  transition: background 0.12s ease;

  &:hover {
    background: ${(p) => (p.$selected ? GOLD_TINT : "#fafafa")};
  }

  .nome {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: -0.01em;
    color: ${TEXT};

    time {
      font-size: 10.5px;
      font-weight: 500;
      color: ${TEXT_MUTED};
      flex-shrink: 0;
    }
  }

  .preview {
    font-size: 12px;
    font-weight: 450;
    color: ${TEXT_SEC};
    margin-top: 4px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.5;
  }

  .meta {
    display: flex;
    gap: 5px;
    margin-top: 8px;
    flex-wrap: wrap;
  }
`;

export const ChatWindow = styled.div`
  background: ${SURFACE};
  border: 1px solid ${BORDER};
  border-radius: ${RADIUS};
  box-shadow: ${SHADOW_SM};
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

export const Avatar = styled.div`
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  background: linear-gradient(135deg, #c8963e, ${GOLD_DARK});
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 800;
  border-radius: ${RADIUS};
`;

export const ChatHeader = styled.div`
  padding: 14px 20px;
  border-bottom: 1px solid ${BORDER};
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;

  .cliente {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .info strong {
    font-size: 14px;
    font-weight: 700;
    letter-spacing: -0.015em;
    color: ${TEXT};
  }
  .info span {
    display: block;
    font-size: 11.5px;
    font-weight: 500;
    color: ${TEXT_MUTED};
    margin-top: 2px;
  }
  .acoes {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
`;

export const ActionButton = styled.button`
  border-radius: ${RADIUS};
  padding: 8px 16px;
  font-family: ${FONT};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: -0.005em;
  cursor: pointer;
  transition: all 0.15s ease;
  background: ${(p) =>
    p.$variant === "gold" ? GOLD : SURFACE};
  color: ${(p) =>
    p.$variant === "gold" ? "#fff" : p.$variant === "danger" ? "#b42318" : TEXT_SEC};
  border: 1px solid
    ${(p) =>
      p.$variant === "gold" ? GOLD : p.$variant === "danger" ? "#fbd5d1" : BORDER};
  box-shadow: ${SHADOW_SM};

  &:hover {
    background: ${(p) =>
      p.$variant === "gold" ? GOLD_DARK : p.$variant === "danger" ? "#fef3f2" : BG};
    border-color: ${(p) =>
      p.$variant === "gold" ? GOLD_DARK : p.$variant === "danger" ? "#f5b5af" : "#d9dbdf"};
  }
  &:active {
    transform: translateY(1px);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const MessagesScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 26px 28px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #fcfcfd;
  ${scrollbars}
`;

export const MsgRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${(p) => (p.$fromIA ? "flex-end" : "flex-start")};
  animation: ${fadeUp} 0.18s ease both;

  .quem {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: ${(p) => (p.$fromIA ? GOLD_DARK : TEXT_MUTED)};
    margin: 0 4px 4px 4px;

    b {
      font-weight: 500;
      text-transform: none;
      letter-spacing: 0;
      color: ${TEXT_MUTED};
      margin-left: 6px;
    }
  }
`;

export const Bubble = styled.div`
  max-width: 62%;
  padding: 11px 15px;
  font-size: 13.5px;
  font-weight: 450;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
  color: ${(p) => (p.$fromIA ? "#3d2f14" : TEXT)};
  background: ${(p) =>
    p.$fromIA
      ? "linear-gradient(180deg, #f8f1e3 0%, #f4ead6 100%)"
      : "#f0f1f3"};
  border: none;
  border-radius: ${(p) =>
    p.$fromIA ? "10px 2px 10px 10px" : "2px 10px 10px 10px"};

  strong {
    font-weight: 700;
    color: ${(p) => (p.$fromIA ? "#2c2210" : TEXT)};
  }

  @media (max-width: 900px) {
    max-width: 85%;
  }
`;

export const InputRow = styled.div`
  display: flex;
  gap: 10px;
  padding: 14px 16px;
  border-top: 1px solid ${BORDER};
  background: ${SURFACE};

  input,
  textarea {
    flex: 1;
    background: ${BG};
    border: 1px solid ${BORDER};
    border-radius: ${RADIUS};
    color: ${TEXT};
    padding: 11px 14px;
    font-family: ${FONT};
    font-size: 13.5px;
    outline: none;
    resize: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;

    &:focus {
      background: ${SURFACE};
      border-color: ${GOLD};
      box-shadow: 0 0 0 3px rgba(177, 125, 42, 0.1);
    }
    &::placeholder {
      color: ${TEXT_MUTED};
    }
  }
`;

export const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: ${TEXT_MUTED};
  font-size: 13px;
  font-weight: 450;
  line-height: 1.7;
  gap: 6px;
  padding: 30px;
  text-align: center;

  strong {
    color: ${TEXT_SEC};
    font-size: 14.5px;
    font-weight: 700;
    letter-spacing: -0.01em;
  }
`;

export const LoadingText = styled.div`
  color: ${TEXT_MUTED};
  padding: 32px;
  text-align: center;
  font-size: 13px;
  font-weight: 500;
`;

export const NewChatButton = styled.button`
  margin: 14px;
  padding: 11px;
  background: ${GOLD};
  color: #fff;
  border: 1px solid ${GOLD};
  border-radius: ${RADIUS};
  font-family: ${FONT};
  font-weight: 700;
  font-size: 12.5px;
  letter-spacing: -0.005em;
  cursor: pointer;
  box-shadow: ${SHADOW_SM};
  transition: all 0.15s ease;

  &:hover {
    background: ${GOLD_DARK};
    border-color: ${GOLD_DARK};
  }
  &:active {
    transform: translateY(1px);
  }
`;

/* ---------------- TICKETS ---------------- */

export const TicketsHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;

  h3 {
    margin: 0;
    font-size: 17px;
    font-weight: 800;
    letter-spacing: -0.02em;
    color: ${TEXT};
  }
  small {
    display: block;
    font-size: 12px;
    font-weight: 500;
    color: ${TEXT_MUTED};
    margin-top: 3px;
  }
  .contagem {
    font-size: 12px;
    font-weight: 600;
    color: ${TEXT_MUTED};
    font-variant-numeric: tabular-nums;
  }
`;

export const TicketStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 10px;
  margin-bottom: 20px;
`;

export const TicketStatCard = styled.button`
  background: ${(p) => (p.$active ? GOLD_TINT : SURFACE)};
  border: 1px solid ${(p) => (p.$active ? GOLD : BORDER)};
  border-left: 3px solid ${(p) => p.$cor || GOLD};
  border-radius: ${RADIUS};
  box-shadow: ${(p) => (p.$active ? `0 0 0 3px rgba(177,125,42,0.1)` : SHADOW_SM)};
  padding: 14px 16px;
  text-align: left;
  font-family: ${FONT};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    box-shadow: ${SHADOW_MD};
    transform: translateY(-1px);
  }

  label {
    display: block;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: ${TEXT_MUTED};
    margin-bottom: 6px;
    cursor: pointer;
  }

  strong {
    font-size: 24px;
    font-weight: 800;
    letter-spacing: -0.02em;
    color: ${TEXT};
    font-variant-numeric: tabular-nums;
    line-height: 1;
  }

  span {
    font-size: 10.5px;
    font-weight: 600;
    color: ${(p) => (p.$active ? GOLD_DARK : TEXT_MUTED)};
    margin-left: 8px;
  }
`;

const CHEVRON_SVG = `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239298a3' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`;

const controlBase = css`
  height: 42px;
  background: ${SURFACE};
  border: 1px solid ${BORDER};
  border-radius: ${RADIUS};
  color: ${TEXT};
  font-family: ${FONT};
  font-size: 13px;
  font-weight: 500;
  outline: none;
  box-shadow: ${SHADOW_SM};
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: #d3d6db;
  }
  &:focus {
    border-color: ${GOLD};
    box-shadow: 0 0 0 3px rgba(177, 125, 42, 0.12);
  }
`;

export const TicketsToolbar = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  flex-wrap: wrap;

  input {
    ${controlBase};
    flex: 2;
    min-width: 280px;
    padding: 0 16px;

    &::placeholder {
      color: ${TEXT_MUTED};
      font-weight: 450;
    }
  }

  select {
    ${controlBase};
    flex: 1;
    min-width: 170px;
    padding: 0 38px 0 14px;
    cursor: pointer;
    appearance: none;
    -webkit-appearance: none;
    background-image: ${CHEVRON_SVG};
    background-repeat: no-repeat;
    background-position: right 14px center;
  }
`;

export const TicketDetail = styled.div`
  background: ${SURFACE};
  border: 1px solid ${GOLD_BORDER};
  border-left: 3px solid ${GOLD};
  border-radius: ${RADIUS};
  box-shadow: ${SHADOW_MD};
  overflow: hidden;
  animation: ${fadeUp} 0.2s ease both;

  .corpo {
    display: grid;
    grid-template-columns: 1.6fr 1fr;
    gap: 0;

    @media (max-width: 800px) {
      grid-template-columns: 1fr;
    }
  }

  .descricao {
    padding: 20px 24px;

    p {
      margin: 0;
      font-size: 13.5px;
      font-weight: 450;
      line-height: 1.7;
      color: ${TEXT};
      white-space: pre-wrap;
    }
  }

  .meta {
    padding: 20px 24px;
    border-left: 1px solid ${BORDER_SOFT};
    display: flex;
    flex-direction: column;
    gap: 14px;

    @media (max-width: 800px) {
      border-left: none;
      border-top: 1px solid ${BORDER_SOFT};
    }

    b {
      display: block;
      font-size: 13px;
      font-weight: 600;
      color: ${TEXT};
      margin-top: 2px;
    }
  }

  label {
    display: block;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.09em;
    color: ${TEXT_MUTED};
    margin-bottom: 6px;
  }

  .acoes {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    padding: 14px 24px;
    border-top: 1px solid ${BORDER_SOFT};
    background: #fbfbfc;

    label {
      margin: 0 6px 0 0;
    }
  }
`;

export const StatusButton = styled.button`
  font-family: ${FONT};
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.01em;
  padding: 7px 14px;
  border-radius: ${RADIUS};
  cursor: pointer;
  transition: all 0.15s ease;
  background: ${(p) =>
    ({
      pendente: "#fffaeb",
      analise: "#eff6ff",
      resolvido: "#f0faf4",
      cancelado: "#f4f5f6",
    }[p.$tipo] || "#f4f5f6")};
  color: ${(p) =>
    ({
      pendente: "#b54708",
      analise: "#1d4ed8",
      resolvido: "#087443",
      cancelado: "#5f6570",
    }[p.$tipo] || "#5f6570")};
  border: 1px solid
    ${(p) =>
      ({
        pendente: "#fce9bb",
        analise: "#d5e3fb",
        resolvido: "#c6ecd5",
        cancelado: "#e7e8ea",
      }[p.$tipo] || "#e7e8ea")};

  &:hover {
    filter: brightness(0.96);
    transform: translateY(-1px);
    box-shadow: ${SHADOW_SM};
  }
  &:active {
    transform: translateY(0);
  }
`;

export const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 16px;

  span {
    font-size: 12px;
    font-weight: 500;
    color: ${TEXT_MUTED};
  }

  button {
    background: ${SURFACE};
    border: 1px solid ${BORDER};
    border-radius: ${RADIUS};
    color: ${TEXT_SEC};
    font-family: ${FONT};
    font-size: 12px;
    font-weight: 700;
    padding: 8px 14px;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover:not(:disabled) {
      background: ${BG};
      border-color: #d9dbdf;
    }
    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }
`;

export const Badge = StatusChip;
